import React from 'react';
import './LowboyAssignmentDay.scss';
import { withRouter } from 'react-router-dom';
import { LinearProgress } from '@mui/material';
import { util } from '../Util';
import { ApiRoutes } from '../ApiRoutes';
import {
    Alert,
    Button,
    Card,
    CardBody,
    CardHeader,
    CardSubtitle,
    Container,
} from 'reactstrap';
import LowboyRequestCardBody from '../lowboyDispatch/LowboyRequestCardBody';
import moment from 'moment';
import { MomentDateFormat } from '../MomentDateFormat';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faClipboardCheck,
    faInfoCircle,
    faLongArrowLeft,
    faThumbsUp,
} from '@fortawesome/free-solid-svg-icons';
import { AppNavPaths } from '../AppNavPaths';
import { LowboyAssignmentStatuses } from '../lowboyDispatch/LowboyAssignmentStatuses';

class LowboyAssignmentDay extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            lowboyAssignments: [],
            equipments: [],
            equipmentTypes: [],
            equipmentLocations: [],
            forDate: new Date(),
            saving: false,
            lowboyTimesheetId: null,
        };

        this.onConfirmClick = this.onConfirmClick.bind(this);
        this.backToCalendar = this.backToCalendar.bind(this);
    }

    componentDidMount() {
        this.populateState();
    }

    async populateState() {
        const eventDate = this.props.match.params.eventDate;

        const [
            lowboyAssignments,
            equipments,
            equipmentTypes,
            equipmentLocations,
            lowboyTimesheetId,
        ] = await Promise.all([
            util.fetch.get(ApiRoutes.lowboyAssignment.dispatches(eventDate)),
            util.fetch.get(ApiRoutes.equipment.all()),
            util.fetch.get(ApiRoutes.equipmentType.all()),
            util.fetch.get(ApiRoutes.equipmentLocation.for(eventDate)),
            util.fetch.js(ApiRoutes.lowboyTimesheet.getIdFor(eventDate)),
        ]);

        this.setState({
            lowboyAssignments,
            loading: false,
            equipments,
            equipmentTypes,
            equipmentLocations,
            lowboyTimesheetId,
            forDate: moment(eventDate).toDate(),
        });
    }

    areAnyAssignmentsNotConfirmed() {
        const { lowboyAssignments } = this.state;

        return lowboyAssignments.some(
            (la) =>
                la.lowboyAssignmentStatusId < LowboyAssignmentStatuses.Confirmed
        );
    }

    async onConfirmClick() {
        const { forDate } = this.state;

        const apiDate = moment(forDate).format(MomentDateFormat.ApiUrlRequest);

        this.setState({ saving: true });

        try {
            await util.fetch.andGetResponse(
                util.fetch.types.post,
                ApiRoutes.lowboyAssignment.confirm(apiDate),
                { apiDate }
            );

            const lowboyAssignments = await util.fetch.get(
                ApiRoutes.lowboyAssignment.dispatches(apiDate)
            );

            this.setState({ lowboyAssignments });
        } catch (e) {
            console.error({ e });
        } finally {
            this.setState({ saving: false });
        }
    }

    backToCalendar() {
        const { history } = this.props;

        history.push(AppNavPaths.AssignmentCalendar);
    }

    onContinueToTimesheet = () => {
        const { history } = this.props;
        const { lowboyTimesheetId } = this.state;

        history.push(`${AppNavPaths.LowboyTimesheet}/${lowboyTimesheetId}`);
    };

    renderBackButton() {
        const { saving } = this.state;

        return (
            <Button
                type="button"
                size="lg"
                block={true}
                outline={true}
                color="success"
                onClick={this.backToCalendar}
                disabled={saving}
            >
                <FontAwesomeIcon icon={faLongArrowLeft} /> Back to calendar
            </Button>
        );
    }

    render() {
        const {
            loading,
            lowboyAssignments,
            equipments,
            equipmentTypes,
            forDate,
            saving,
        } = this.state;

        if (loading) {
            return <LinearProgress variant="indeterminate" color="secondary" />;
        }

        const anyAssignmentsNotConfirmed = this.areAnyAssignmentsNotConfirmed();

        const dateColor = anyAssignmentsNotConfirmed ? 'warning' : 'success';

        const groupedByJob = lowboyAssignments.reduce((groups, la) => {
            const jobId = la.lowboyResource.jobId;
            if (!groups[jobId]) {
                groups[jobId] = {
                    contractName: la.lowboyResource.contractName,
                    jobNotes: la.lowboyResource.jobNotes,
                    startTimeFormatted: moment(
                        `${la.lowboyResource.startTime}Z`
                    )
                        .local()
                        .format('h:mm A'),
                    assignments: [],
                };
            }
            groups[jobId].assignments.push(la);
            return groups;
        }, {});

        return (
            <Container className="assignments-container big-container ml-auto mr-auto">
                {saving && (
                    <LinearProgress variant="indeterminate" color="secondary" />
                )}

                <div className="mt-3 mb-1">{this.renderBackButton()}</div>

                <Alert color={dateColor} fade={false} className="m-2">
                    Here are your assignments for{' '}
                    {moment(forDate).format(
                        MomentDateFormat.WeekDayNameAndDate
                    )}
                    .
                </Alert>

                {Object.entries(groupedByJob).map(([jobId, group]) => (
                    <Card id={`job-card-${jobId}`} key={jobId} className="mt-3">
                        <CardHeader className="h4 p-2">
                            {group.assignments.length} Assignment
                            {group.assignments.length > 1 && <>s</>} for{' '}
                            {group.contractName}
                        </CardHeader>
                        {group.jobNotes && (
                            <CardSubtitle className="mt-2 ml-2">
                                <FontAwesomeIcon
                                    icon={faInfoCircle}
                                    className="mr-1"
                                />
                                Notes from the PM:
                                <div>
                                    <pre>{group.jobNotes}</pre>
                                </div>
                            </CardSubtitle>
                        )}
                        <CardBody>
                            <div className="flex-assignments-container">
                                {group.assignments.map((la) => {
                                    const equipment = equipments.find(
                                        (eq) => eq.id == la.equipmentId
                                    );

                                    const equipmentType = equipmentTypes.find(
                                        (et) =>
                                            et.id ==
                                            la.lowboyResource.equipmentTypeId
                                    );

                                    return (
                                        <Card
                                            key={la.id}
                                            className="mt-2 m-2 flex-assignment"
                                        >
                                            <LowboyRequestCardBody
                                                lowboyAssignment={la}
                                                lowboyResource={
                                                    la.lowboyResource
                                                }
                                                equipment={equipment}
                                                equipmentType={equipmentType}
                                                {...this.state}
                                            />
                                        </Card>
                                    );
                                })}
                            </div>
                        </CardBody>
                    </Card>
                ))}

                {Boolean(!lowboyAssignments || !lowboyAssignments.length) && (
                    <Card className="mb-2">
                        <CardBody>No assignments found.</CardBody>
                    </Card>
                )}

                {anyAssignmentsNotConfirmed && (
                    <div className="mt-5 mb-2">
                        <Button
                            type="button"
                            color="success"
                            block
                            size="lg"
                            onClick={this.onConfirmClick}
                            disabled={saving}
                            outline={saving}
                        >
                            <FontAwesomeIcon
                                icon={faThumbsUp}
                                className="mr-2"
                            />{' '}
                            {!saving && (
                                <>Click here to confirm your assignments.</>
                            )}
                            {saving && <>Confirming assignments...</>}
                        </Button>
                    </div>
                )}

                {!anyAssignmentsNotConfirmed && (
                    <div>
                        <Card className="mb-2 mt-2">
                            <CardBody>
                                <FontAwesomeIcon
                                    icon={faThumbsUp}
                                    className="mr-2"
                                />
                                Assignments are all confirmed.
                            </CardBody>
                        </Card>

                        <div className="mt-1 mb-5">
                            <Button
                                onClick={this.onContinueToTimesheet}
                                color="success"
                                className="w-100"
                                size="lg"
                            >
                                <FontAwesomeIcon
                                    icon={faClipboardCheck}
                                    className="mr-2"
                                />{' '}
                                Continue to Timesheet
                            </Button>
                        </div>

                        <div className="mt-1 mb-2">
                            {this.renderBackButton()}
                        </div>
                    </div>
                )}

                {saving && (
                    <LinearProgress variant="indeterminate" color="secondary" />
                )}
            </Container>
        );
    }
}

export default withRouter(LowboyAssignmentDay);
